import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { FileText, Film, Music, Archive, FileQuestion, Image as ImageIcon, type LucideIcon } from 'lucide-react';
import type { AssetKind } from '@shared/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Absolute URL for an app-relative path, against the origin actually being
 * served — devdam.aicotone.bio, dam.aicotone.bio or localhost.
 *
 * Deliberately uses `window.location.origin` rather than plumbing the server's
 * PUBLIC_URL to the client: the page is already loaded from the right host, so
 * this can't drift the way a second copy of the hostname in env config can.
 */
export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof window === 'undefined') return path;
  return new URL(path, window.location.origin).href;
}

/** Short absolute date, e.g. "3 Aug 2026, 14:22". */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * MIME types a browser can decode in an <img> tag.
 *
 * `kind` alone is not enough to decide this. Pimcore types Illustrator files as
 * kind='image' with mimetype='application/pdf' — 4,593 assets on prod — and
 * pointing an <img> at the raw file shows a broken-image icon. Anything not on
 * this list needs the rendered thumbnail instead.
 */
const DISPLAYABLE_MIME = /^image\/(png|jpeg|jpg|gif|webp|avif|svg\+xml|bmp)$/i;

/** True when the original bytes can be shown directly, without rasterising. */
export function canDisplayRaw(mime: string | null): boolean {
  return Boolean(mime) && DISPLAYABLE_MIME.test(mime!);
}

/** Icon + tint per asset kind, for grid tiles without a thumbnail. */
export const KIND_ICON: Record<AssetKind, LucideIcon> = {
  folder: FileQuestion,
  image: ImageIcon,
  document: FileText,
  video: Film,
  audio: Music,
  archive: Archive,
  text: FileText,
  unknown: FileQuestion,
};

/**
 * Per-kind icon tints. These are a categorical key, not brand colours — each
 * kind needs to be tellable apart at a glance, so they stay distinct hues.
 *
 * Images deliberately avoid green: they are the overwhelming majority of the
 * library (1,430 of 1,437 on dev), and a green icon on every row would read as
 * a brand accent rather than a file-type signal.
 */
export const KIND_TINT: Record<AssetKind, string> = {
  folder: 'text-slate-400',
  image: 'text-teal-600',
  document: 'text-red-500',
  video: 'text-violet-500',
  audio: 'text-amber-500',
  archive: 'text-orange-500',
  text: 'text-sky-500',
  unknown: 'text-slate-400',
};

/**
 * Placeholders the AI tagger writes when it can't identify a field, in the
 * decorated forms it actually emits: `None`, `{None}`, `** None **`.
 *
 * They are real stored strings, not empty values, so a plain `if (brand)` check
 * passes and the UI renders a field reading "{None}".
 *
 * The decoration is stripped before comparing, mirroring `cleanTagValue` in
 * server/pimcore-datasource.ts — the same `*` and `{}` noise the search filters
 * already normalise away. Keep the two in step.
 */
const EMPTY_TAG = /^(none|n\/a|na|unknown|null|undefined|-|—)$/i;

/**
 * A tag value worth showing, or undefined when it is absent or a placeholder.
 * Returns the CLEANED text, so `{Pack Shot}` displays as `Pack Shot`.
 */
export function tagText(v: string | null | undefined): string | undefined {
  const s = (v ?? '')
    .replace(/\*+/g, '')
    .replace(/[{}]/g, '')
    .trim();
  return s && !EMPTY_TAG.test(s) ? s : undefined;
}
